import { combineReducers, configureStore, Reducer } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import audiencesReducer, {
    AudiencesState,
    initialState as audiencesInitial
} from './audiences/audiencesSlice';
import authReducer, { AuthState, initialState as authInitial } from './auth/authSlice';
import menuManagerReducer, {
    MenuManagerState,
    initialState as menuManagerInitial
} from './menu-manager/menuManagerSlice';
import contentReducer, { ContentState, initialState as contentInitial } from './content/contentSlice';
import locationsReducer, {
    initialState as locationInitial,
    LocationsState
} from './locations/locationsSlice';
import manifestReducer, { initialState as manifestInitial, ManifestState } from './manifest/manifestSlice';
import stripeSettingsReducer, {
    initialState as stripeSettingsInitial,
    StripeSettingsState
} from './stripeSettings/stripeSettingsSlice';

import perksReducer, { initialState as perksInitial, PerksState } from './perks/perksSlice';
import tenantAccessReducer, {
    initialState as tenantAccessInitial,
    TenantAccessState
} from './tenant-access/tenantAccessSlice';
import uiReducer, { initialState as uiInitial, UiState } from './ui/uiSlice';
import settingsReducer, { initialState as settingsInitial, SettingsState } from './settings/settingsSlice';
import matchCodesReducer, {
    initialState as matchCodesInitial,
    MatchCodesState
} from './match-codes/matchCodesSlice';
import posLocationsReducer, {
    PosLocationsState,
    initialState as posLocationsInitial
} from './pos-locations/posLocationsSlice';

export interface ApplicationState {
    auth: AuthState;
    audiences: AudiencesState;
    ui: UiState;
    locations: LocationsState;
    tenantAccess: TenantAccessState;
    content: ContentState;
    manifest: ManifestState;
    perks: PerksState;
    stripeSettings: StripeSettingsState;
    settings: SettingsState;
    menuManager: MenuManagerState;
    matchCodes: MatchCodesState;
    posLocations: PosLocationsState;
}

const appReducer = combineReducers({
    auth: authReducer,
    audiences: audiencesReducer,
    ui: uiReducer,
    locations: locationsReducer,
    tenantAccess: tenantAccessReducer,
    content: contentReducer,
    manifest: manifestReducer,
    perks: perksReducer,
    stripeSettings: stripeSettingsReducer,
    settings: settingsReducer,
    menuManager: menuManagerReducer,
    matchCodes: matchCodesReducer,
    posLocations: posLocationsReducer
});

const emptyState: ApplicationState = {
    auth: authInitial,
    audiences: audiencesInitial,
    ui: uiInitial,
    locations: locationInitial,
    tenantAccess: tenantAccessInitial,
    content: contentInitial,
    manifest: manifestInitial,
    perks: perksInitial,
    stripeSettings: stripeSettingsInitial,
    settings: settingsInitial,
    menuManager: menuManagerInitial,
    matchCodes: matchCodesInitial,
    posLocations: posLocationsInitial
};

const rootReducer: Reducer<ApplicationState> = (state = emptyState, action) => {
    // when a logout action is dispatched it will reset redux state
    if (action.type === 'DESTROY_SESSION') {
        // tslint:disable-next-line
        state = { ...emptyState, settings: state.settings, auth: state.auth };
    }

    return appReducer(state, action);
};

// Init store
export const initStore = (initialState = emptyState) =>
    configureStore({
        reducer: rootReducer,
        preloadedState: initialState,
        middleware: getDefaultMiddleware => {
            const mw = getDefaultMiddleware({
                serializableCheck: false
            });
            if (import.meta.env.NODE_ENV !== 'production') {
                mw.push(createLogger());
            }
            return mw;
        }
    });

export type AppDispatch = ReturnType<typeof initStore>['dispatch'];
export type RootState = ApplicationState;
